import { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { APIContext } from 'context/APIContext'
import { ModalContext } from '../ModalContext'
import { eventPriorityOptions, GetSportTerrainOptions, GetSportDistanceOptions } from 'functions/eventUtils'
import { GetPrimaryColorHex } from 'redux/slices/brand'
import moment from 'moment'

import BrandColorButton from 'components/Buttons/BrandColorButton'
import OutlineButton from 'components/Buttons/OutlineButton'

export default function KinesisEventV5Modal() {

    const { GetSports } = useContext(APIContext)
    const { modalData } = useContext(ModalContext)

    // 'create-event' | 'edit-event' | 'create-simulator-event' | 'edit-simulator-event'
    const mode = modalData.mode
    const kinesisEvent = modalData.kinesisEvent ?? null

    // Save and delete actions are passed in by the opener and return an error message on failure
    const SaveEvent = modalData.SaveEvent
    const DeleteEvent = modalData.DeleteEvent

    const editMode = mode === 'edit-event' || mode === 'edit-simulator-event'
    const simulatorMode = mode === 'create-simulator-event' || mode === 'edit-simulator-event'

    // Editing a saved event only allows changing the event date. Simulator events can edit everything
    const lockedEditMode = mode === 'edit-event'

    const styleConfig = useSelector((state) => state.brand.styleConfig)
    const trainingChartPresentDate = useSelector((state) => state.athleteProfile.trainingChartPresentDate)
    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)
    const presentDate = simulatorMode ? moment().format('YYYY-MM-DD') : trainingChartPresentDate
    const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')

    // Simulator defaults to a b-race so events can be added quickly
    const [eventName, setEventName] = useState(kinesisEvent?.name ?? (simulatorMode ? 'BRACE' : ''))
    const [eventPriority, setEventPriority] = useState(kinesisEvent?.eventType ?? (simulatorMode ? 'b-race' : ''))
    const [sports, setSports] = useState(null)
    const [selectedSportKey, setSelectedSportKey] = useState(kinesisEvent?.sportKey ?? '')
    const [selectedEventTerrain, setSelectedEventTerrain] = useState(kinesisEvent?.eventTerrain ?? '')
    const [eventTerrainTextInput, setEventTerrainTextInput] = useState(kinesisEvent?.eventTerrainTextInput ?? '')
    const [selectedEventDistance, setSelectedEventDistance] = useState(kinesisEvent?.eventDistance ?? '')
    const [eventDistanceTextInput, setEventDistanceTextInput] = useState(kinesisEvent?.eventDistanceTextInput ?? '')
    const [eventTrainingStartDate, setEventTrainingStartDate] = useState(kinesisEvent?.eventTrainingStartDate ?? moment(presentDate).startOf('isoWeek').format('YYYY-MM-DD'))
    const [eventDate, setEventDate] = useState(editMode ? (kinesisEvent?.eventDate ?? '') : moment(presentDate).add(16, 'week').format('YYYY-MM-DD'))
    const [deloadAfterEvent, setDeloadAfterEvent] = useState(kinesisEvent?.deloadAfterEvent ?? 1)
    const [formErrorMessage, setFormErrorMessage] = useState('')
    const [isSavingEventLoading, setIsSavingEventLoading] = useState(false)
    const [isDeletingEventLoading, setIsDeletingEventLoading] = useState(false)

    // The simulator only supports one a-race (it defines the program)
    const simulatorARaceExists = simulatorMode && (modalData.kinesisEvents ?? []).some((event) => event.eventType === 'a-race' && event.id !== kinesisEvent?.id)

    const sportTerrainOptions = GetSportTerrainOptions(selectedSportKey)
    const sportDistanceOptions = GetSportDistanceOptions(selectedSportKey)

    // Terrain and distance don't affect the simulated program, so they're bypassed in simulator mode
    const showSportTerrainOptions = !simulatorMode && sportTerrainOptions.length > 0
    const showSportDistanceOptions = !simulatorMode && sportDistanceOptions.length > 0
    const prioritySelected = eventPriority !== ''

    // Saved events always show their details, even legacy ones without a priority
    const showEventDetails = prioritySelected || lockedEditMode
    const selectedPriorityDescription = eventPriorityOptions.find((option) => option.key === eventPriority)?.description ?? ''

    const lockedInputClassName = lockedEditMode ? 'opacity-50 cursor-not-allowed' : ''

    const InitializeSports = async () => {
        setSports(null)
        const sports = await GetSports()
        setSports(sports)
    }

    const HandleFormChange = (key, value) => {
        if (formErrorMessage !== '') {
            setFormErrorMessage('')
        }

        switch (key) {
            case 'eventName':
                setEventName(value)
                break

            case 'eventPriority':
                setEventPriority(value)
                if (value === 'b-race' || value === 'c-race') setEventTrainingStartDate(moment(presentDate).startOf('isoWeek').format('YYYY-MM-DD'))
                if (simulatorMode && value === 'b-race') setEventName('BRACE')
                if (simulatorMode && value === 'c-race') setEventName('CRACE')
                break

            case 'selectedSportKey':
                setSelectedSportKey(value)
                setSelectedEventTerrain('')
                setEventTerrainTextInput('')
                setSelectedEventDistance('')
                setEventDistanceTextInput('')
                break

            case 'selectedEventTerrain':
                setSelectedEventTerrain(value)
                setEventTerrainTextInput('')
                break

            case 'eventTerrainTextInput':
                setEventTerrainTextInput(value)
                break

            case 'selectedEventDistance':
                setSelectedEventDistance(value)
                setEventDistanceTextInput('')
                break

            case 'eventDistanceTextInput':
                setEventDistanceTextInput(value)
                break

            case 'eventTrainingStartDate':
                setEventTrainingStartDate(value)
                break

            case 'eventDate':
                setEventDate(value)
                break

            default:
                console.log('Unknown form change key:', key)
                break
        }
    }

    const ValidateFormInputs = () => {

        // Locked fields come from the saved event, so only the event date is validated when editing one
        if (!lockedEditMode) {

            if (eventName.length === 0) {
                setFormErrorMessage('Event name is required')
                return false
            }

            if (eventPriority.length === 0) {
                setFormErrorMessage('Event priority is required')
                return false
            }

            if (eventPriority === 'a-race' && simulatorARaceExists) {
                setFormErrorMessage('Only one A race can be simulated. Remove the current A race first')
                return false
            }

            if (selectedSportKey === '' && !simulatorMode) {
                setFormErrorMessage('Event sport is required')
                return false
            }

            if (showSportTerrainOptions && selectedEventTerrain.length === 0) {
                setFormErrorMessage('Event terrain is required')
                return false
            }

            if (showSportTerrainOptions && selectedEventTerrain === 'other' && eventTerrainTextInput.length === 0) {
                setFormErrorMessage('Event terrain is required')
                return false
            }

            if (showSportDistanceOptions && selectedEventDistance.length === 0) {
                setFormErrorMessage('Event distance is required')
                return false
            }

            if (showSportDistanceOptions && selectedEventDistance === 'other' && eventDistanceTextInput.length === 0) {
                setFormErrorMessage('Event distance is required')
                return false
            }

        }

        if (eventDate.length === 0) {
            setFormErrorMessage('Event date is required')
            return false
        }

        if (eventPriority === 'a-race') {

            // Make sure eventTrainingStartDate is before eventDate
            if (moment(eventTrainingStartDate).isAfter(moment(eventDate))) {
                setFormErrorMessage('Event date must be after event training start week')
                return false
            }

            // Make sure eventTrainingStartDate is not on the same ISO week as eventDate
            if (moment(eventTrainingStartDate).isoWeek() === moment(eventDate).isoWeek()) {
                setFormErrorMessage('Event date cannot be during the event training start week')
                return false
            }

            // Make sure the number of days between eventTrainingStartDate and eventDate is less than 360
            if (moment(eventDate).diff(moment(eventTrainingStartDate), 'days') > 360) {
                setFormErrorMessage('Event training start week and event date must be within 360 days of each other')
                return false
            }

        }

        return true
    }

    const OnSaveEventButton = async () => {
        const formValid = ValidateFormInputs()
        if (!formValid) return

        // Edited events keep their id and any fields this form doesn't touch
        const newEvent = {
            ...(kinesisEvent ?? {}),
            name: eventName,
            eventType: eventPriority,
            sportId: sports.find((sport) => sport.key === selectedSportKey)?.id ?? null,
            sportKey: selectedSportKey,
            eventTerrain: selectedEventTerrain,
            eventTerrainTextInput: eventTerrainTextInput,
            eventDistance: selectedEventDistance,
            eventDistanceTextInput: eventDistanceTextInput,
            eventTrainingStartDate: eventTrainingStartDate,
            eventDate: eventDate
        }

        if (simulatorMode) {
            newEvent.deloadAfterEvent = eventPriority === 'a-race' ? deloadAfterEvent : 0
        }

        setIsSavingEventLoading(true)

        const saveErrorMessage = await SaveEvent(newEvent)

        if (saveErrorMessage) {
            setFormErrorMessage(saveErrorMessage)
            setIsSavingEventLoading(false)
        }
    }

    const OnDeleteEventButton = async () => {
        setIsDeletingEventLoading(true)

        const deleteErrorMessage = await DeleteEvent()

        if (deleteErrorMessage) {
            setFormErrorMessage(deleteErrorMessage)
            setIsDeletingEventLoading(false)
        }
    }

    useEffect(() => { InitializeSports() }, [])

    if (sports === null) {
        return (
            <div className='min-h-[min(44rem,85vh)] space-y-4'>
                <div className='flex-shrink-0 h-16 bg-gray-200 rounded-lg animate-pulse' />
                <div className='flex-shrink-0 h-16 bg-gray-200 rounded-lg animate-pulse' />
                <div className='flex-shrink-0 h-16 bg-gray-200 rounded-lg animate-pulse' />
                <div className='flex-shrink-0 h-16 bg-gray-200 rounded-lg animate-pulse' />
            </div>
        )
    }

    return (
        <div className='flex flex-col h-full min-h-[min(44rem,85vh)] space-y-4 text-left'>
            <div className='text-center text-xl font-semibold leading-6'>
                {editMode ? 'Edit Event' : 'Event Information'}
            </div>

            <div className='space-y-1'>
                <p className='pl-2 text-xs font-bold text-gray-500'>Name</p>

                <input
                    type="text"
                    className={`w-full h-12 rounded-full bg-gray-100 px-4 text-gray-900 placeholder:text-gray-400 focus:outline-none ${lockedInputClassName}`}
                    placeholder='Enter event name here'
                    value={eventName}
                    onChange={(event) => HandleFormChange('eventName', event.target.value)}
                    disabled={lockedEditMode}
                />
            </div>

            <div>
                <div className='pl-2 text-xs font-bold text-gray-500'>Priority</div>

                <div className={`mt-1 h-12 items-center grid grid-cols-3 rounded-full divide-x-2 divide-white overflow-hidden text-center bg-gray-100 ${lockedInputClassName}`}>
                    {eventPriorityOptions.map((option) => (
                        <div
                            key={option.key}
                            className={`
                                h-12 flex items-center justify-center
                                ${!lockedEditMode && option.key === 'a-race' && simulatorARaceExists ? 'cursor-not-allowed opacity-40' : ''}
                                ${!lockedEditMode && !(option.key === 'a-race' && simulatorARaceExists) ? 'cursor-pointer' : ''}
                                ${eventPriority === option.key ? 'text-white font-bold' : 'text-gray-400'}
                            `}
                            style={{ backgroundColor: eventPriority === option.key ? GetPrimaryColorHex(styleConfig) : '' }}
                            onClick={() => {
                                if (lockedEditMode) return
                                if (option.key === 'a-race' && simulatorARaceExists) return
                                HandleFormChange('eventPriority', option.key)
                            }}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>

                <div
                    className={`
                        grid min-h-0 transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none
                        ${prioritySelected ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}
                    `}
                >
                    <div className="overflow-hidden min-h-0">
                        <p className='pt-1 pl-2 text-xs text-gray-500'>{selectedPriorityDescription}</p>
                    </div>
                </div>
            </div>

            <div
                className={`
                    grid min-h-0 transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none
                    ${showEventDetails ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}
                `}
                inert={showEventDetails ? undefined : ''}
                aria-hidden={showEventDetails ? undefined : true}
            >
                <div className="overflow-hidden min-h-0">
                    <div className='flex flex-col'>
                        {/* Sport doesn't affect the simulated program, so it's bypassed in simulator mode */}
                        {!simulatorMode && (
                            <div className='space-y-1'>
                                <p className='pl-2 text-xs font-bold text-gray-500'>Sport</p>

                                <select
                                    id="eventSport"
                                    value={selectedSportKey}
                                    onChange={(e) => HandleFormChange('selectedSportKey', e.target.value)}
                                    className={`w-full h-12 px-4 rounded-full text-gray-900 bg-gray-100 ${lockedInputClassName}`}
                                    disabled={lockedEditMode}
                                >
                                    <option disabled value="">Select Sport</option>

                                    {sports.map((sport) => (
                                        <option key={sport.key} value={sport.key}>
                                            {sport.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div
                            className={`
                                grid min-h-0 transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none
                                ${showSportTerrainOptions ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}
                            `}
                            inert={showSportTerrainOptions ? undefined : ''}
                            aria-hidden={showSportTerrainOptions ? undefined : true}
                        >
                            <div className="overflow-hidden min-h-0">
                                <div className="space-y-1 pt-4">
                                    <p className='pl-2 text-xs font-bold text-gray-500'>Terrain</p>

                                    <div className='flex items-center gap-x-2'>
                                        <select
                                            id="eventTerrain"
                                            value={selectedEventTerrain}
                                            onChange={(e) => HandleFormChange('selectedEventTerrain', e.target.value)}
                                            className={`
                                                ${selectedEventTerrain === 'other' ? 'w-1/3' : 'w-full'}
                                                h-12 px-4 rounded-full text-gray-900 bg-gray-100 ${lockedInputClassName}
                                            `}
                                            disabled={lockedEditMode}
                                        >
                                            <option disabled value="">Select Event Type</option>

                                            {sportTerrainOptions.map((sportTerrainOption, index) => (
                                                <option
                                                    key={index}
                                                    value={sportTerrainOption.key}
                                                >
                                                    {sportTerrainOption.title}
                                                </option>
                                            ))}
                                        </select>

                                        {selectedEventTerrain === 'other' && (
                                            <input
                                                type="text"
                                                className={`w-2/3 h-12 rounded-full bg-gray-100 px-4 text-gray-900 placeholder:text-gray-400 focus:outline-none ${lockedInputClassName}`}
                                                placeholder='Enter other terrain here'
                                                value={eventTerrainTextInput}
                                                onChange={(event) => HandleFormChange('eventTerrainTextInput', event.target.value)}
                                                disabled={lockedEditMode}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`
                                grid min-h-0 transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none
                                ${showSportDistanceOptions ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}
                            `}
                            inert={showSportDistanceOptions ? undefined : ''}
                            aria-hidden={showSportDistanceOptions ? undefined : true}
                        >
                            <div className="overflow-hidden min-h-0">
                                <div className="space-y-1 pt-4">
                                    <p className='pl-2 text-xs font-bold text-gray-500'>Distance</p>

                                    <div className='flex items-center gap-x-2'>
                                        <select
                                            id="eventDistance"
                                            value={selectedEventDistance}
                                            onChange={(e) => HandleFormChange('selectedEventDistance', e.target.value)}
                                            className={`
                                                ${selectedEventDistance === 'other' ? 'w-1/3' : 'w-full'}
                                                h-12 px-4 rounded-full text-gray-900 bg-gray-100 ${lockedInputClassName}
                                            `}
                                            disabled={lockedEditMode}
                                        >
                                            <option disabled value="">Select Event Distance</option>

                                            {sportDistanceOptions.map((sportDistanceOption, index) => (
                                                <option
                                                    key={index}
                                                    value={sportDistanceOption.key}
                                                >
                                                    {sportDistanceOption.title}
                                                </option>
                                            ))}
                                        </select>

                                        {selectedEventDistance === 'other' && (
                                            <input
                                                type="text"
                                                className={`w-2/3 h-12 rounded-full bg-gray-100 px-4 text-gray-900 placeholder:text-gray-400 focus:outline-none ${lockedInputClassName}`}
                                                placeholder='Enter other distance here'
                                                value={eventDistanceTextInput}
                                                onChange={(event) => HandleFormChange('eventDistanceTextInput', event.target.value)}
                                                disabled={lockedEditMode}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`
                                grid min-h-0 transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none
                                ${eventPriority === 'a-race' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}
                            `}
                            inert={eventPriority === 'a-race' ? undefined : ''}
                            aria-hidden={eventPriority === 'a-race' ? undefined : true}
                        >
                            <div className="overflow-hidden min-h-0">
                                <div className="space-y-1 pt-4">
                                    <p className='pl-2 text-xs font-bold text-gray-500'>Training Start Week</p>

                                    <p className='pl-2 text-xs text-gray-500'>Which week would you like to begin strength programming for this event?</p>

                                    {simulatorMode && (
                                        <input
                                            type="date"
                                            id="startWeek"
                                            value={eventTrainingStartDate}
                                            onChange={(event) => HandleFormChange('eventTrainingStartDate', event.target.value)}
                                            className="w-full h-12 px-4 rounded-full text-gray-900 bg-gray-100"
                                        />
                                    )}

                                    {simulatorMode && (
                                        <label className='flex items-center gap-x-2 pl-2 pt-2 text-xs text-gray-500'>
                                            <input
                                                type='checkbox'
                                                id='deloadAfterEvent'
                                                checked={deloadAfterEvent === 1}
                                                onChange={(event) => setDeloadAfterEvent(event.target.checked ? 1 : 0)}
                                            />
                                            Post event deload
                                        </label>
                                    )}

                                    {/* Saved events keep their training start week */}
                                    {lockedEditMode && (
                                        <select
                                            id="startWeek"
                                            value={eventTrainingStartDate}
                                            className={`w-full h-12 px-4 rounded-full text-gray-900 bg-gray-100 ${lockedInputClassName}`}
                                            disabled
                                        >
                                            <option value={eventTrainingStartDate}>
                                                {moment(eventTrainingStartDate).startOf('isoWeek').format('MMMM D')} - {moment(eventTrainingStartDate).endOf('isoWeek').format('MMMM D')}
                                            </option>
                                        </select>
                                    )}

                                    {mode === 'create-event' && <select
                                        id="startWeek"
                                        value={eventTrainingStartDate}
                                        onChange={(event) => HandleFormChange('eventTrainingStartDate', event.target.value)}
                                        className="w-full h-12 px-4 rounded-full text-gray-900 bg-gray-100"
                                    >
                                        {Array.from({ length: 7 }, (_, i) => i).map((i) => (
                                            <option
                                                key={i}
                                                value={moment(presentDate).add(i, 'isoWeeks').startOf('isoWeek').format('YYYY-MM-DD')}
                                            >
                                                {moment(presentDate).add(i, 'isoWeeks').startOf('isoWeek').format('MMMM D')} - {moment(presentDate).add(i, 'isoWeeks').endOf('isoWeek').format('MMMM D')}
                                            </option>
                                        ))}
                                    </select>}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1 pt-4">
                            <p className='pl-2 text-xs font-bold text-gray-500'>Event Date</p>

                            <input
                                type="date"
                                name="eventDate"
                                id="eventDate"
                                placeholder="Event Date*"
                                value={eventDate}
                                onChange={(event) => HandleFormChange('eventDate', event.target.value)}
                                min={simulatorMode ? undefined : (lockedEditMode ? clientDate : presentDate)}
                                className="w-full h-12 px-4 rounded-full text-gray-900 bg-gray-100"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className='mt-auto w-full space-y-4'>
                {formErrorMessage !== '' && <p className="mt-1 text-sm text-center text-red-600">{formErrorMessage}</p>}

                <BrandColorButton
                    size="large"
                    text={mode === 'create-simulator-event' ? 'Add Event' : 'Save Event'}
                    action={OnSaveEventButton}
                    isLoading={isSavingEventLoading}
                />

                {editMode && (
                    <OutlineButton
                        size="large"
                        text="Delete Event"
                        borderColor='border-red-500'
                        textColor='text-red-500'
                        action={OnDeleteEventButton}
                        isLoading={isDeletingEventLoading}
                    />
                )}
            </div>
        </div>
    )
}
